import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/api";
import { money, dateFr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, Trash, Eye, Truck, Paperclip, FilePdf, Key, CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";

const empty = { name: "", email: "", phone: "", address: "", notes: "", opening_balance: 0 };

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [portalFor, setPortalFor] = useState(null);
  const [portalPw, setPortalPw] = useState("");

  const load = () => api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const totalDue = suppliers.reduce((s, x) => s + x.balance, 0);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ name: s.name, email: s.email || "", phone: s.phone || "", address: s.address || "", notes: s.notes || "", opening_balance: s.opening_balance || 0 }); setEditId(s.id); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Le nom est requis."); return; }
    const payload = { ...form, opening_balance: Number(form.opening_balance) || 0 };
    try {
      if (editId) await api.put(`/suppliers/${editId}`, payload);
      else await api.post("/suppliers", payload);
      toast.success(editId ? "Fournisseur mis à jour" : "Fournisseur ajouté"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => { try { await api.delete(`/suppliers/${delId}`); toast.success("Fournisseur supprimé"); setDelId(null); load(); } catch (e) { toast.error(apiError(e)); } };
  const openDetail = async (s) => { try { const { data } = await api.get(`/suppliers/${s.id}`); setDetail(data); } catch (e) { toast.error(apiError(e)); } };

  const sendInvite = async (s) => {
    if (!s.email) { toast.error("Ajoutez d'abord un courriel au fournisseur."); return; }
    try {
      const { data } = await api.post(`/suppliers/${s.id}/invite`);
      if (data.already_registered) toast.info("Ce fournisseur possède déjà un compte.");
      else if (data.email_sent) toast.success("Invitation envoyée — le fournisseur recevra un lien pour créer son compte.");
      else toast.warning("Invitation créée, mais courriel non envoyé (SMTP non configuré).");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const savePortal = async () => {
    if (portalPw.length < 6) { toast.error("Mot de passe : min. 6 caractères."); return; }
    try {
      const { data } = await api.post(`/suppliers/${portalFor.id}/portal-access`, { password: portalPw });
      toast.success(data.email_sent ? "Accès créé — courriel avec le lien de connexion envoyé au fournisseur" : "Accès créé (courriel non envoyé : SMTP non configuré)");
      setPortalFor(null); setPortalPw(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Fournisseurs</h1>
          <p className="text-muted-foreground mt-1">Comptes fournisseurs, factures reçues et soldes dus.</p>
        </div>
        <Button data-testid="new-supplier-btn" className="rounded-sm" onClick={openNew}><Plus size={18} className="mr-1" /> Nouveau fournisseur</Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="border border-border bg-card p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Solde dû total</div>
          <div className="mt-2 font-heading text-2xl font-bold tabular-nums text-red-600" data-testid="total-due">{money(totalDue)}</div>
        </div>
        <div className="border border-border bg-card p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Fournisseurs</div>
          <div className="mt-2 font-heading text-2xl font-bold tabular-nums">{suppliers.length}</div>
        </div>
        <div className="border border-border bg-card p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Total facturé</div>
          <div className="mt-2 font-heading text-2xl font-bold tabular-nums">{money(suppliers.reduce((s, x) => s + x.total_billed, 0))}</div>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Fournisseur</TableHead><TableHead>Courriel</TableHead>
              <TableHead className="text-right">Factures</TableHead><TableHead className="text-right">Total facturé</TableHead>
              <TableHead className="text-right">Payé</TableHead><TableHead className="text-right">Solde dû</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {suppliers.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Aucun fournisseur. Ils sont créés automatiquement lors de l'ajout d'une dépense.</TableCell></TableRow>}
            {suppliers.map((s) => (
              <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <span>{s.name}</span>
                    {s.invite_status === "registered" && <span data-testid={`invite-status-${s.id}`} className="text-[10px] px-1.5 py-0.5 rounded-sm border bg-emerald-50 text-emerald-700 border-emerald-200">Inscrit</span>}
                    {s.invite_status === "invited" && <span data-testid={`invite-status-${s.id}`} className="text-[10px] px-1.5 py-0.5 rounded-sm border bg-amber-50 text-amber-700 border-amber-200">Invité</span>}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{s.email || "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{s.invoices_count}</TableCell>
                <TableCell className="text-right tabular-nums">{money(s.total_billed)}</TableCell>
                <TableCell className="text-right tabular-nums text-emerald-700">{money(s.total_paid)}</TableCell>
                <TableCell className={`text-right tabular-nums font-medium ${s.balance > 0 ? "text-red-600" : ""}`}>{money(s.balance)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button data-testid={`statement-${s.id}`} variant="ghost" size="icon" className="h-8 w-8" title="Relevé PDF" onClick={() => window.open(`${API}/suppliers/${s.id}/statement?auth=${localStorage.getItem("token")}`, "_blank")}><FilePdf size={16} /></Button>
                    <Button data-testid={`invite-supplier-${s.id}`} variant="ghost" size="icon" className="h-8 w-8" title="Inviter le fournisseur à créer son compte" onClick={() => sendInvite(s)}><PaperPlaneTilt size={16} /></Button>
                    <Button data-testid={`portal-supplier-${s.id}`} variant="ghost" size="icon" className="h-8 w-8" title="Définir l'accès manuellement" onClick={() => { setPortalFor(s); setPortalPw(""); }}><Key size={16} className={s.has_portal ? "text-emerald-600" : ""} /></Button>
                    <Button data-testid={`view-supplier-${s.id}`} variant="ghost" size="icon" className="h-8 w-8" title="Voir le compte" onClick={() => openDetail(s)}><Eye size={16} /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(s)}><PencilSimple size={16} /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 hover:text-destructive" onClick={() => setDelId(s.id)}><Trash size={16} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Truck size={20} /> {editId ? "Modifier le fournisseur" : "Nouveau fournisseur"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <F label="Nom *"><Input data-testid="supplier-name" className="rounded-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
            <F label="Courriel (pour confirmations de paiement)"><Input data-testid="supplier-email" type="email" className="rounded-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
            <div className="grid grid-cols-2 gap-4">
              <F label="Téléphone"><Input className="rounded-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></F>
              <F label="Adresse"><Input className="rounded-sm" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></F>
            </div>
            <F label="Solde d'ouverture (montant dû avant l'utilisation du logiciel)"><Input data-testid="supplier-opening-balance" type="number" step="0.01" className="rounded-sm tabular-nums" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} /></F>
            <F label="Notes"><Textarea className="rounded-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-supplier-btn" className="rounded-sm" onClick={save}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{detail?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-4">
                <Card label="Total facturé" value={money(detail.total_billed)} />
                <Card label="Payé" value={money(detail.total_paid)} cls="text-emerald-700" />
                <Card label="Solde dû" value={money(detail.balance)} cls={detail.balance > 0 ? "text-red-600" : ""} />
              </div>
              <div className="text-sm text-muted-foreground">{detail.email || "Aucun courriel"} · {detail.phone || "—"}{detail.opening_balance ? ` · Solde d'ouverture : ${money(detail.opening_balance)}` : ""}</div>
              <div className="border border-border rounded-sm overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="bg-muted/50"><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Statut</TableHead><TableHead>Confirmation</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {(detail.expenses || []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Aucune facture reçue.</TableCell></TableRow>}
                    {(detail.expenses || []).map((e) => (
                      <TableRow key={e.id}>
                        <TableCell className="text-sm text-muted-foreground">{dateFr(e.date)}</TableCell>
                        <TableCell className="text-sm">{e.description || e.vendor}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(e.total)}</TableCell>
                        <TableCell><span className={`text-xs px-2 py-0.5 rounded-sm border ${e.payment_status === "paid" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>{e.payment_status === "paid" ? "Payée" : "À payer"}</span></TableCell>
                        <TableCell className="text-xs font-mono">{e.confirmation_number || "—"}</TableCell>
                        <TableCell className="text-right">{e.attachment_path && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => window.open(`${API}/expenses/${e.id}/attachment?auth=${localStorage.getItem("token")}`, "_blank")}><Paperclip size={16} /></Button>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Portal access */}
      <Dialog open={!!portalFor} onOpenChange={(v) => !v && setPortalFor(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Key size={20} /> Accès portail fournisseur</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Le fournisseur <strong>{portalFor?.name}</strong> pourra se connecter avec son courriel
              <strong> {portalFor?.email || "(aucun courriel — ajoutez-en un d'abord)"}</strong> pour consulter ses factures et confirmations de paiement.
            </p>
            <F label="Mot de passe"><Input data-testid="supplier-portal-password" type="text" className="rounded-sm" placeholder="min. 6 caractères" value={portalPw} onChange={(e) => setPortalPw(e.target.value)} /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setPortalFor(null)}>Annuler</Button>
            <Button data-testid="save-supplier-portal-btn" className="rounded-sm" onClick={savePortal} disabled={!portalFor?.email}>Configurer l'accès</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(v) => !v && setDelId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader><AlertDialogTitle>Supprimer ce fournisseur ?</AlertDialogTitle><AlertDialogDescription>Les dépenses associées seront conservées mais dissociées.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-supplier" className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={del}>Supprimer</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>{children}</div>;
}
function Card({ label, value, cls = "" }) {
  return (
    <div className="border border-border rounded-sm p-4">
      <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
      <div className={`mt-1 font-heading text-xl font-bold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
